<template>
  <div :class="componentClasses" v-if="!isDeleted()">
    <div class="medication__general-info mr-6">
      <div
          class="medication__general-info-row medication__general-info-row-top"
      >
        <medication-administration
            :is-editable="isEditable"
            v-model="administration"
            class="mr-6"
        ></medication-administration>
        <medication-text-field
            :is-editable="isEditable"
            :placeholder="$ucfirst($t('myWeekMedicationDetail.name', {catalog: 'app_mobile'}))"
            :error="!isNamePresent && showErrors"
            v-model="name"
            class="medication__name mr-6"
            :auto-select-all="false"
        ></medication-text-field>
        <div class="medication__class-dosage-interfaces">
          <medication-class-dosage-interface
              v-for="(medicationClass, i) in classesOrRecommendedClasses"
              :key="`${medicationClass}-${i}`"
              :is-removable="classes.length > 1"
              :is-editable="isEditable"
              :medication-class="medicationClass"
              :dosage-value="dosages[i] ? dosages[i].value : ''"
              :dosage-unit="dosages[i] ? dosages[i].unit : 'mg'"
              :dosage-id="dosages[i] ? dosages[i].id : null"
              :administration="administration"
              :show-errors="showErrors"
              :is-recommendation="isUnhandled"
              :recommended-classes="medicationSchedulesWithRecommendationInfo ? medicationSchedulesWithRecommendationInfo.recommendedClasses : []"
              :selected-classes="classes"
              :needs-o-r-suffix="needsORSuffix(i)"
              :has-medication-class="hasMedicationClass"
              :hideRecommendationInfo="!hasRecommendationInfo"
              @update:medicationClass="(v) => updateMedicationClass(v, i)"
              @update:dosageValue="(v) => updateDosageValue(v, i)"
              @update:dosageUnit="(v) => updateDosageUnit(v, i)"
              @remove="() => removeMedicationClass(i)"
          ></medication-class-dosage-interface>
        </div>
        <v-tooltip v-if="isEditable" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
                v-if="!singleClass"
                fab
                text
                small
                color="primary"
                @click="addMedicationClass"
                v-on="on"
                v-bind="attrs"
                class="ml-3"
            >
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("medication.addClass") }}</span>
        </v-tooltip>
      </div>
      <div class="medication__general-info-row">
        <medication-frequency
            :is-editable="isEditable"
            :show-errors="showErrors"
            :schedule="schedule"
            @frequencyChanged="frequencyChanged"
            @input="
            (v) => {
              return v == null ? (schedule = null) : (schedule = v);
            }
          "
        ></medication-frequency>
        <medication-quartiles
            :is-editable="isEditable"
            :morningAmount="morningAmount"
            :noonAmount="noonAmount"
            :afternoonAmount="afternoonAmount"
            :eveningAmount="eveningAmount"
            :error="(!isQuartilesPresent || !isQuartileAmountBetweenBoundaries[0] || !isQuartilesANumber[0]) && showErrors"
            :errorList="getQuartilesErrorListNames"
            @update:morningAmount="updateMorningAmount"
            @update:noonAmount="updateNoonAmount"
            @update:afternoonAmount="updateAfternoonAmount"
            @update:eveningAmount="updateEveningAmount"
            class="mr-6"
        ></medication-quartiles>
      </div>
      <div class="medication__general-info-row" v-if="hasNotes">
        <v-textarea
          :disabled="!isEditable"
          v-model="notes"
          hide-details
          :placeholder="isEditable ? $ucfirst($t('medication.notes')) : $t('questionnaire_mm.no_notes_added', {catalog: 'app_tablet'})"
          outlined
          dense
          class="medication__notes"
          auto-grow
          :rows="1"
        ></v-textarea>
      </div>
      <div
          class="medication__general-info-row"
          v-if="medicationSchedulesWithRecommendationInfo
            && medicationSchedulesWithRecommendationInfo.guidelines.length
            && hasRecommendationInfo"
      >
        <v-expansion-panels class="medication__expansion-panel" focusable>
          <v-expansion-panel>
            <v-expansion-panel-header :color="expansionPanelColor" class="ignoredisableall">
              {{ $t("general.moreInfo", {catalog: 'app_mobile'}) }}
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <span
                  v-for="guideline in medicationSchedulesWithRecommendationInfo.guidelines"
                  v-bind:key="guideline.id"
                  class="mb-3"
              >
                <recommendation-class
                    v-if="isUnhandled || !guideline.medicationClass || classesOrRecommendedClasses.includes(guideline.medicationClass)"
                    :recommendationClass="guideline.classLabel"
                    :levelOfEvidence="guideline.levelOfEvidence"
                    :source="guideline.source"
                    :sourceText="guideline.text"
                    :color="expansionPanelColor"
                />
              </span>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
      <div class="medication__general-info-row" v-if="hasHistory">
        <medication-change-history
            v-if="!isEditable && isHistoryAvailable"
            :medication-id="id"
            :color="expansionPanelColor"
        ></medication-change-history>
      </div>
    </div>
    <div class="medication__controls" v-if="!isReadOnly">
      <v-tooltip v-if="isEditable" bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            class="ml-4"
            fab
            v-on="on"
            v-bind="attrs"
            text
            right
            color="primary"
            @click="submit"
          >
            <eva-icon name="save-outline"></eva-icon>
          </v-btn>
        </template>
        <span>{{ $t("dss.saveMedication") }}</span>
      </v-tooltip>
      <v-tooltip v-else bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
              class="ml-4"
              fab
              v-on="on"
              v-bind="attrs"
              text
              right
              color="primary"
              @click="isEditable = true"
          >
            <eva-icon name="edit-2-outline"></eva-icon>
          </v-btn>
        </template>
        <span>{{ $t("screens.editMedicationScreen", {catalog: "app_mobile"}) }}</span>
      </v-tooltip>
      <div class="d-flex flex-column align-end">
        <medication-remove-button
          v-if="!isUnhandled"
          :medication-id="id"
          :medication-name="name"
          @remove="remove"
        >
        </medication-remove-button>
        <smart-icon
          v-if="isChangedByPatient"
          style="opacity: 0.5"
          icon="mdi-account-edit"
          size="28"
          class="mr-3 mt-4"
        />
      </div>
      <d-s-s-decision-rejection
          v-if="isUnhandled"
          v-on:rejection="softReject"
          :medication-recommendation-id="medicationSchedulesWithRecommendationInfo.medicationRecommendations[0].id"
          showTooltip
      />
    </div>
    <div v-else-if="showPatientIcon">
      <smart-icon
        v-if="isChangedByPatient"
        style="opacity: 0.5"
        icon="mdi-account-edit"
        size="28"
        class="mr-3"
        />
    </div>
    <div class="medication-info-panel" v-if="hasRecommendationInfo">
      <medication-info
          v-if="showErrors"
          color="error"
          :messages="errorsDescription"
          :errorInfo="true"
      ></medication-info>
      <medication-info
          v-if="value.recommended"
          color="success"
          :messages="[$ucfirst($t('dss.recommendedByAlgorithm'))]"
      ></medication-info>
      <medication-info
          v-if="isUnhandled"
          color="warning"
          :messages="[$ucfirst($t('dss.isRecommendation'))]"
      ></medication-info>
    </div>
  </div>
</template>

<script>
import MedicationAdministration from "./MedicationAdministration";
import MedicationTextField from "./MedicationTextField";
import MedicationClassDosageInterface from "./MedicationClassDosageInterface";
import MedicationFrequency from "./MedicationFrequency";
import MedicationQuartiles from "./MedicationQuartiles";
import MedicationRemoveButton from "./MedicationRemoveButton";
import MedicationInfo from "./MedicationInfo";
import MedicationChangeHistory from "./MedicationChangeHistory.vue";
import RecommendationClass from "../DSS/RecommendationClass.vue";
import DSSDecisionRejection from '../DSS/DSSDecisionRejection.vue';
import SmartIcon from '../SmartIcon.vue';
import {PILL, INJECTION} from "./administration";
import app from '../../../../app';
import gql from "graphql-tag";
import {mapGetters} from "vuex";
import {isNumeric} from "../../../../../helper/validation";
import {UCUMUnit} from "../../../../../helper/UCUMUnit";

/**
 * Component Medication.vue
 *
 * @module Medication
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.10] app/src/APP_DASHBOARD/templates/components/Parts/Medication/Medication.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: "Medication",
  components: {
    MedicationAdministration,
    MedicationTextField,
    MedicationFrequency,
    MedicationQuartiles,
    MedicationClassDosageInterface,
    MedicationRemoveButton,
    MedicationInfo,
    MedicationChangeHistory,
    RecommendationClass,
    DSSDecisionRejection,
    SmartIcon
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    // medicationList: {
    //   type: Array,
    //   required: true,
    // },
    index: {
      type: Number,
      required: true,
    },
    singleClass: {
      type: Boolean,
      default: () => false,
    },
    pulsing: {
      type: Boolean,
      default: () => false,
    },
    hasMedicationClass: {
      type: Boolean,
      default: () => true,
    },
    color: {
      type: String,
      default: "",
    },
    isReadOnly: {
      type: Boolean,
      default: () => false,
    },
    medicationSchedulesWithRecommendationInfo: {
      type: Object,
      default: () => null,
    },
    hasNotes: {
      type: Boolean,
      default: () => true,
    },
    hasHistory: {
      type: Boolean,
      default: () => true,
    },
    hasRecommendationInfo: {
      type: Boolean,
      default: () => true,
    },
    isNew: {
      type: Boolean,
      default: () => false,
    },
    showPatientIcon: {
      type: Boolean,
      default: () => false
    }
  },
  data() {
    return {
      isEditable: false,
      showErrors: false,
      id: "",
      name: "",
      type: "cardio",
      administration: PILL,
      defaultAdministrationUnits: {
        [PILL]: UCUMUnit.MG.unit,
        [INJECTION]: UCUMUnit.ML.unit,
      },
      dosages: [],
      intakeMoments: [],
      schedule: {
        intervals: [],
        monthsOfYear: [],
        daysOfMonth: [],
        daysOfWeek: [],
        weeksOfMonth: [],
        weeksOfYear: [],
        anchorDate: "",
        times: [],
        note: "",
      },
      classes: [],
      morningAmount: 0,
      noonAmount: 0,
      afternoonAmount: 0,
      eveningAmount: 0,
      notes: "",
      combinedDrugs: [],
      auditTrails: [],
      softDeletedId: null,
      timeOutDelete: null,
      recommendationId: '',
      rejectionDetails: {}
    };
  },
  computed: {
    ...mapGetters({
      patientId: "newPatient/subjectId",
      auditTrailsNodes: "dss/auditTrailsNodes",
      allMedicationClasses: "dss/medicationClasses",
      unsavedChanges: 'patientSummary/unsavedChanges'
    }),
    isUnhandled(){
      return this?.medicationSchedulesWithRecommendationInfo?.isUnhandled;
    },
    classesOrRecommendedClasses(){
        if (this.isUnhandled && !this.isEditable){
           const recommendedClasses = this?.medicationSchedulesWithRecommendationInfo?.recommendedClasses || [];
           return recommendedClasses.map(recommendedClass => recommendedClass.key);
        }else{
          return this.classes;
        }
    },
    isHistoryAvailable() {
      return this.auditTrailsNodes.some(
          (node) => node.medicationScheduleId === this.id
      );
    },
    expansionPanelColor() {
      if (this.color === "green") {
        return "rgba(76, 175, 80, 0.08)";
      } else if (this.color === "yellow") {
        return "rgba(243, 174, 61, 0.08)";
      } else {
        return "rgb(246, 246, 246)";
      }
    },
    componentClasses() {
      const color = `medication--${this.color}`;
      return {
        medication: true,
        "pulse-add-medication" : this.pulsing,
        "medication--editable": this.isEditable,
        ...(this.color && {[color]: true}),
      };
    },
    validationMessages() {
      const quartilesErr = this.getQuartilesErrorList();
      const quartilesInvalidNumberNames = quartilesErr?.isQuartilesANumber || [];
      const amountQuartileIncorrectNames = quartilesErr?.isQuartilesAmountBetweenBoundaries || [];

      return {
        nameRequired: `
          ${this.$t('myWeekMedicationDetail.name', {catalog: 'app_mobile'})}:
          ${this.$t("validation.IS_REQUIRED")}
        `,
        dosageRequired: `
          ${this.$t('myWeekMedicationDetail.dosage', {catalog: 'app_mobile'})}:
          ${this.$t("validation.IS_REQUIRED")}
        `,
        dosageInvalidNumber: `
          ${this.$t('myWeekMedicationDetail.dosage', {catalog: 'app_mobile'})}:
          ${this.$t('dummy.general.invalid_number', {catalog: 'validators'})}
        `,
        dosagePositive: `
          ${this.$t('myWeekMedicationDetail.dosage', {catalog: 'app_mobile'})}:
          ${this.$t('validation.onlyPositiveNumbers')}
        `,
        classRequired: `
          ${this.$t('medication.medicationClass')}:
          ${this.$t("validation.IS_REQUIRED")}
        `,
        quartilesRequired: `
          ${this.$t('myWeekMedicationDetail.schedule', {catalog: 'app_mobile'})}:
          ${this.$t("validation.IS_REQUIRED")}
        `,
        quartilesInvalidNumber: quartilesInvalidNumberNames.map(item => {
          return `
            ${this.$t('myWeekMedicationDetail.schedule', {catalog: 'app_mobile'})}
            ${this.$t(`quartiles.${item}`)}:
            ${this.$t('dummy.general.invalid_number', {catalog: 'validators'})}
          `
        }),
        amountQuartileIncorrect: amountQuartileIncorrectNames.map(item => {
          return `
            ${this.$t('myWeekMedicationDetail.schedule', {catalog: 'app_mobile'})}
            ${this.$t(`quartiles.${item}`)}:
            ${this.$t('dummy.healthparameters.number_of_intake', {catalog: 'validators', params: { min: '0.25', max: '10' }})}
          `
        }),
        frequencyValueRequired: `
          ${this.$t('pdf.medicationPrescription.table.intakeFrequency', {catalog: 'messages'})}:
          ${this.$t("validation.IS_REQUIRED")}
        `,
        frequencyValuePositive: `
          ${this.$t('pdf.medicationPrescription.table.intakeFrequency', {catalog: 'messages'})}:
          ${this.$t("validation.onlyPositiveNumbers")}
        `,
      };
    },
    isNamePresent() {
      return !!this.name;
    },
    isDosagePresent() {
      return this.dosages.every((item) => +item.value);
    },
    isDosageANumber() {
      if (!this.isDosagePresent) {
        return true
      }
      return this.dosages.every((item) => item.value && !Number.isNaN(Number(item.value)));
    },
    isDosagePositive() {
      if (!this.isDosagePresent || !this.isDosageANumber) {
        return true
      }
      return this.dosages.every((item) => +item.value > 0);
    },
    isClassPresent() {
      return this.classes.length && this.classes.every(item => item);
    },
    isQuartilesPresent() {
      return [
        this.morningAmount,
        this.noonAmount,
        this.afternoonAmount,
        this.eveningAmount,
      ].some((item) => +item)
    },
    isQuartilesANumber() {
      if (!this.isQuartilesPresent) {
        return [true]
      }
      let morningAmountOk = isNumeric(this.morningAmount)
      let noonAmountOk = isNumeric(this.noonAmount)
      let afternoonAmountOk = isNumeric(this.afternoonAmount)
      let eveningAmountOk = isNumeric(this.eveningAmount)

      const notOk = [];

      if (!morningAmountOk) {
        notOk.push('morning')
      }
      if (!noonAmountOk) {
        notOk.push('noon')
      }
      if (!afternoonAmountOk) {
        notOk.push('afternoon')
      }
      if (!eveningAmountOk) {
        notOk.push('evening')
      }

      return notOk.length ? [false, ...notOk] : [true];
    },
    isQuartileAmountBetweenBoundaries() {
      let min = 0.25;
      let max = 10;

      let morningAmountOk = isNumeric(this.morningAmount) ?
        (parseFloat(this.morningAmount) === 0 || (parseFloat(this.morningAmount) >= min && parseFloat(this.morningAmount) <= max))
        : [true]
      let noonAmountOk = isNumeric(this.noonAmount) ?
        (parseFloat(this.noonAmount) === 0 || (parseFloat(this.noonAmount) >= min && parseFloat(this.noonAmount) <= max))
        : [true]
      let afternoonAmountOk = isNumeric(this.afternoonAmount) ?
        (parseFloat(this.afternoonAmount) === 0 || (parseFloat(this.afternoonAmount) >= min && parseFloat(this.afternoonAmount) <= max))
        : [true]
      let eveningAmountOk = isNumeric(this.eveningAmount) ?
        (parseFloat(this.eveningAmount) === 0 || (parseFloat(this.eveningAmount) >= min && parseFloat(this.eveningAmount) <= max))
        : [true]

      const notOk = [];

      if (!morningAmountOk) {
        notOk.push('morning')
      }
      if (!noonAmountOk) {
        notOk.push('noon')
      }
      if (!afternoonAmountOk) {
        notOk.push('afternoon')
      }
      if (!eveningAmountOk) {
        notOk.push('evening')
      }
      return notOk.length ? [false, ...notOk] : [true];
    },
    isFrequencyValuePresent() {
      if (this.schedule && this.schedule.intervals && this.schedule.intervals[0]) {
        const intervalText = this.schedule.intervals[0].interval;
        const period = intervalText[intervalText.length - 1];

        if ((!parseInt(intervalText.substr(1, 1)) && parseInt(intervalText.substr(1, 1)) !== 0) ||
            (period === 'W' && !this.schedule.daysOfWeek.length) ||
            (period === 'M' && !this.schedule.daysOfMonth) ||
            (period === 'Y' && (!this.schedule.monthsOfYear || !this.schedule.daysOfMonth))
        ) {
          return false
        }
      }
      return true
    },
    isFrequencyValuePositive() {
      if (!this.isFrequencyValuePresent) {
        return true
      }
      if (this.schedule && this.schedule.intervals && this.schedule.intervals[0]) {
        const intervalText = this.schedule.intervals[0].interval;
        if (parseInt(intervalText.substr(1, 1)) <= 0) {
          return false
        }
      }
      return true
    },
    isValid() {
      return [
        this.isNamePresent,
        this.isDosagePresent,
        this.isDosageANumber,
        this.isDosagePositive,
        this.isClassPresent,
        this.isQuartilesPresent,
        this.isQuartilesANumber[0],
        this.isQuartileAmountBetweenBoundaries[0],
        this.isFrequencyValuePresent,
        this.isFrequencyValuePositive
      ].every((item) => item);
    },
    errorsDescription() {
      return [
        ...(this.isNamePresent ? [] : [this.validationMessages.nameRequired]),
        ...(this.isDosagePresent ? [] : [this.validationMessages.dosageRequired]),
        ...(this.isDosageANumber ? [] : [this.validationMessages.dosageInvalidNumber]),
        ...(this.isDosagePositive ? [] : [this.validationMessages.dosagePositive]),
        ...(this.isClassPresent ? [] : [this.validationMessages.classRequired]),
        ...(this.isQuartilesPresent ? [] : [this.validationMessages.quartilesRequired]),
        ...(this.isQuartilesANumber[0] ? [] : [...this.validationMessages.quartilesInvalidNumber]),
        ...(this.isQuartileAmountBetweenBoundaries[0] ? [] : [...this.validationMessages.amountQuartileIncorrect]),
        ...(this.isFrequencyValuePresent ? [] : [this.validationMessages.frequencyValueRequired]),
        ...(this.isFrequencyValuePositive ? [] : [this.validationMessages.frequencyValuePositive])
      ];
    },
    getQuartilesErrorListNames() {
      const errorList = this.getQuartilesErrorList();
      return errorList.quartiles;
    },
    isChangedByPatient() {
      const currentMedicationAudits = this.$store.getters['dss/DSSLastAuditTrails']
        .find(item => item.id === this.value?.id);

      return !!currentMedicationAudits?.lastAuditTrail
    }
  },
  watch: {
    isValid(isValid) {
      if (isValid) {
        this.showErrors = false;
      }
    },
    value(value) {
      if (this.needReseting()) {
        Object.assign(this, {
          id: value.id,
          name: value.name,
          administration: value.administration,
          dosages: value.dosages,
          schedule: value.schedule,
          classes: value.classes,
          morningAmount: value.morningAmount,
          noonAmount: value.noonAmount,
          afternoonAmount: value.afternoonAmount,
          eveningAmount: value.eveningAmount,
          notes: value.notes,
          combinedDrugs: value.combinedDrugs,
        });

        if (this.classes.length === 0) {
          this.classes = [""];
        }

        if (this.dosages.length === 0) {
          this.dosages = [{value: "", unit: UCUMUnit.MG.unit, id : null}];
        }
      }
    },
    administration() {
      this.dosages.forEach(dosage => {
        if (!dosage.value && this.defaultAdministrationUnits[this.administration]) {
          dosage.unit = this.defaultAdministrationUnits[this.administration];
        }
      });
    }
  },
  methods: {
    setIsEditable(isEditable) {
      this.isEditable = isEditable;
    },
    needsORSuffix(index){
      if (this.isUnhandled && !this.isEditable){
        return index < this.classesOrRecommendedClasses.length - 1;
      }

      return false;
    },
    addMedicationClass() {
      const filledDosages = this.dosages.filter(item => +item.value)
      const lastFilledDosage = filledDosages[filledDosages.length-1]
      const lastDosage = this.dosages[this.dosages.length-1]

      this.classes = [...this.classes, ""];
      this.dosages = [...this.dosages, {...lastFilledDosage || lastDosage}]
      // New medication row does not have an id.
      this.dosages[this.dosages.length - 1].id = null;
    },
    removeMedicationClass(index) {
      this.classes = this.classes.filter((item, i) => i !== index);
      this.dosages = this.dosages.filter((item, i) => i !== index);
    },
    updateMorningAmount(amount) {
      this.morningAmount = amount;
    },
    updateNoonAmount(amount) {
      this.noonAmount = amount;
    },
    updateAfternoonAmount(amount) {
      this.afternoonAmount = amount;
    },
    updateEveningAmount(amount) {
      this.eveningAmount = amount;
    },
    updateMedicationClass(medicationClass, index) {
      this.classes = this.classes.map((item, i) => {
        if (i === index) {
          return medicationClass;
        }
        return item;
      });
    },
    updateDosageValue(dosageValue, index) {
      this.dosages = this.dosages.map((item, i) => {
        if (i === index) {
          return Object.assign(item, {value: dosageValue});
        }

        return item;
      });
    },
    updateDosageUnit(dosageUnit, index) {
      this.dosages = this.dosages.map((item, i) => {
        if (i === index) {
          return Object.assign(item, {unit: dosageUnit});
        }

        return item;
      });
    },
    submit() {
      if (!this.isValid) {
        this.showErrors = true;
        return;
      }
      this.recommendationId = this.medicationSchedulesWithRecommendationInfo
        .medicationRecommendations[0]?.id || '';

      this.$emit("input", {
        id: this.id,
        name: this.name,
        type: this.type,
        administration: this.administration,
        notes: this.notes,
        dosages: this.dosages,
        intakeMoments: this.intakeMoments,
        schedule: this.schedule,
        patientId: this.patientId,
        classes: this.getClasses(),
        morningAmount: this.morningAmount,
        noonAmount: this.noonAmount,
        afternoonAmount: this.afternoonAmount,
        eveningAmount: this.eveningAmount,
        combinedDrugs: this.combinedDrugs,
      }, this);

    },
    remove() {
      this.$emit("remove", this.id);
    },
    getClasses() {
      return this.classes.filter((val) => val !== "");
    },
    reject(data, otherText, medicationRecommendationId) {
      app.apolloClient
          .mutate({
            mutation: gql`
            mutation reject(
              $medicationRecommendationId: String!
              $types: [String!]
              $other: String
            ) {
              rejectDSSRecommendation(
                medicationRecommendationId : $medicationRecommendationId
                types : $types
                other : $other
              ){
                id
                types
                other
                medicationRecommendations {
                  id
                }
              }
            }
          `,
            variables: {
              medicationRecommendationId: medicationRecommendationId,
              types: data,
              other: otherText
            }
          })
          .then(result => {
            const rejectedMedicationRecommendations = result.data.rejectDSSRecommendation.medicationRecommendations;

            for (let i=0; i<rejectedMedicationRecommendations.length; i++) {
              this.$store.commit('dss/removeUnhandledMedicationRecommendationById', rejectedMedicationRecommendations[i].id)
            }

            // app.store.dispatch('dss/getDSSMedicationSchedules');
            this.$store.dispatch('dss/setSchedulesToBeDeleted', {add:false})
          })
    },
    getQuartilesErrorList() {
      if (!this.isQuartilesPresent) {
        return { quartiles: ['morning', 'afternoon', 'noon', 'evening'] }
      }
      const notOk = [];
      const notOkNum = [];
      const notOkRange = [];

      if (!this.isQuartilesANumber[0]) {
        const notNum = [...this.isQuartilesANumber]
        notNum.shift();
        notOk.push(...notNum)
        notOkNum.push(...notNum)
      }
      if (!this.isQuartileAmountBetweenBoundaries[0]) {
        const notInRange = [...this.isQuartileAmountBetweenBoundaries]
        notInRange.shift();
        const unique = notInRange.filter(i => !notOk.includes(i))
        notOk.push(...unique)
        notOkRange.push(...notInRange)
      }
      return {
        quartiles: notOk,
        isQuartilesANumber: notOkNum,
        isQuartilesAmountBetweenBoundaries: notOkRange
      }
    },
    softReject(data, otherText, id) {
      this.softDeletedId = id
      this.rejectionDetails = { id, data, otherText }
      this.$store.dispatch('dss/setSchedulesToBeDeleted', {add:true})
      const classes = this.medicationSchedulesWithRecommendationInfo.recommendedClasses;
      const classNames = classes.map(item => this.getMedicationClassName(item.key)).join(', ')
      const isUnsaved = this.isUnsaved(`medication-${id}`);

      // 'Undo' toast
      this.$toasted.show(
        `${this.$ucfirst(
          this.$t("dss.classHasBeenDeleted", {
            params: {
              medicationClass: classNames
            },
          })
        )}
        `,
        {
          icon: 'undo',
          duration: 10000,
          action: {
            text: this.$ucfirst(
                this.$t("dss.undo")
            ),
            onClick: (e, toastObject) => {
              toastObject.goAway(0);
              this.clearTimeoutDelete()
              this.$store.dispatch('dss/setSchedulesToBeDeleted', {add:false})
              if (isUnsaved) {
                this.toggleUnsaved(id, true)
              }
            }
          },
        }
      )

      const timeout = setTimeout(() => {
        this.reject(data, otherText, id);
      }, 10000);

      this.timeOutDelete = timeout
      this.toggleUnsaved(id, false)
    },
    clearTimeoutDelete() {
      clearTimeout(this.timeOutDelete);
      this.timeOutDelete = null
      this.softDeletedId = null
    },
    getMedicationClassName(key) {
      const className = this.allMedicationClasses.find(item => item.key === key);
      return className?.name || key;
    },
    isDeleted() {
      const id = this.medicationSchedulesWithRecommendationInfo.medicationRecommendations[0]?.id;
      return this.softDeletedId === id
    },
    needReseting() {
      // reset needed only if recommended medication was saved without the recommended class
      // or if it's medication prescription screen
      const id = this.medicationSchedulesWithRecommendationInfo.medicationRecommendations[0]?.id || '';
      const recommendedClasses = this?.medicationSchedulesWithRecommendationInfo?.recommendedClasses.map(i => i.key)
      const classesToSave = this.getClasses();
      const savedRecommendationClasses = classesToSave.some(classToSave => recommendedClasses.includes(classToSave))
      const isMedicationPrescriptionRoute = this.$route.name === 'DSSMedicationPrescription'

      return (this.isUnhandled && !savedRecommendationClasses && id === this.recommendationId) || isMedicationPrescriptionRoute;
    },
    setInputWatchers() {
      const inputKeys = [
        'administration',
        'name',
        'dosages',
        'classes',
        'morningAmount',
        'noonAmount',
        'afternoonAmount',
        'eveningAmount',
        'notes'
      ];

      const recommendationId = this.medicationSchedulesWithRecommendationInfo
        .medicationRecommendations[0]?.id || '';

      const id = this.id ? this.id : recommendationId ? recommendationId : this.isNew ? 'new' : this.index;

      inputKeys.forEach((key) => {
        this.$watch(key, {
          deep: true,
          handler: function() {
            const data = {
              unsavedChanges: true,
              screen: 'cardioMedication',
              name: `medication-${id}`
            }
            if (this.isEditable) {
              this.$store.dispatch('patientSummary/toggleMultipleUnsavedChanges', data);
            }
          }
        })
      })
    },
    frequencyChanged() {
      const recommendationId = this.medicationSchedulesWithRecommendationInfo
        .medicationRecommendations[0]?.id || '';
      const id = this.id ? this.id : recommendationId ? recommendationId : this.isNew ? 'new' : this.index;
      this.toggleUnsaved(id, true)
    },
    toggleUnsaved(id, unsaved) {
      const data = {
        unsavedChanges: unsaved,
        screen: 'cardioMedication',
        name: `medication-${id}`
      }
      this.$store.dispatch('patientSummary/toggleMultipleUnsavedChanges', data);
    },
    isUnsaved(name) {
      const allUnsaved = this.unsavedChanges('cardioMedication', true);
      const unsaved = allUnsaved.find(item => item.name === name)
      return !!unsaved
    }
  },
  created() {
    if (!this.isNew) {
      Object.assign(this, {
        id: this.value.id,
        name: this.value.name,
        administration: this.value.administration,
        dosages: this.value.dosages.map(item => JSON.parse(JSON.stringify(item))),
        schedule: this.value.schedule,
        classes: this.value.classes,
        morningAmount: this.value.morningAmount,
        noonAmount: this.value.noonAmount,
        afternoonAmount: this.value.afternoonAmount,
        eveningAmount: this.value.eveningAmount,
        notes: this.value.notes,
        combinedDrugs: this.value.combinedDrugs,
        auditTrails: this.auditTrails,
      });
    }

    if (this.value.isNew) {
      this.isEditable = true;
    }

    if (this.classes.length === 0) {
      this.classes = [""];
    }

    if (this.dosages.length === 0) {
      this.dosages = [{value: "", unit: UCUMUnit.MG.unit}];
    }
  },
  mounted() {
    if (!this.id) {
      this.recommendationId = this.medicationSchedulesWithRecommendationInfo
        .medicationRecommendations[0]?.id || '';
    }

    this.setInputWatchers();
  }
};
</script>

<style lang="scss">
.medication {
  display: flex;
  justify-content: space-between;
  border: 1px solid #d6dbe4;
  border-radius: 16px;
  padding: 24px;
  min-width: 1200px;
  margin-bottom: 24px;
  position: relative;

  .v-text-field--outlined fieldset {
    border-style: none;
  }

  .v-input__append-inner {
    display: none;
  }

  .v-text-field--outlined {
    background-color: #f6f6f6;
  }

  &--editable {
    .v-text-field--outlined fieldset {
      border-style: solid;
    }

    .v-input__append-inner {
      display: flex;
    }

    .v-text-field--outlined {
      background-color: transparent !important;
    }
  }

  .v-select__selection--disabled {
    color: #222b45 !important;
  }

  input, textarea {
    color: #222b45 !important;
  }
}

.medication__general-info-row {
  display: flex;
  align-items: center;

  &:not(:last-child) {
    margin-bottom: 24px;
  }
}

.medication__general-info-row-top {
  align-items: flex-start;
}

.medication__name {
  width: 200px;
}

.medication__dosage {
  width: 100px;

  input {
    text-align: right;
  }
}

.medication__notes {
  flex-grow: 1;
  textarea {
    resize: none;
    margin-bottom: 6px;
  }
}

.medication__controls {
  display: flex;
  justify-content: flex-end;
}

.medication--yellow {
  background-color: rgba(243, 174, 61, 0.08);
  border-color: #f7cc85;

  .v-text-field--outlined {
    background-color: rgba(243, 174, 61, 0.08);
  }

  .medication-frequency__input_disabled {
    background-color: rgba(243, 174, 61, 0.1);
  }
}

.medication--green {
  background-color: rgba(76, 175, 80, 0.08);
  border-color: #80c883;

  .v-text-field--outlined {
    background-color: rgb(76, 175, 80, 0.08);
  }

  .medication-frequency__input_disabled {
    background-color: rgba(76, 175, 80, 0.1);
  }

  .v-expansion-panel-header__icon {
    background-color: rgba(76, 175, 80, 0.08) !important;
  }
}

.medication-info-panel {
  display: flex;
  position: absolute;
  bottom: 24px;
  right: 24px;
}

.pulse-add-medication {
  animation: pulseAddMedication 0.3s infinite alternate;
}

@keyframes pulseAddMedication {
  from {
    border-color: #d6dbe4;
  }
  to {
    border-color: #3D0A5E;
  }
}
</style>
